/* eslint-disable jsx-a11y/no-autofocus */
import React from "react";

import Paper from "@material-ui/core/Paper";
import clsx from "clsx";
import PropTypes from "prop-types";

import RbActionsContext from "components/RbActions/RbActionsContext";
import RbButtonAction from "components/RbButtonAction";
import RbPopper from "components/RbPopper";

import useStyles from "./RbMenuAction.styles";

export const RbMenuAction = React.forwardRef(function RbMenuAction(props, ref) {
  const { items, selectedItem, onSelect, onClick, ...other } = props;

  const classes = useStyles();

  const { insideMore, compacted, color } = React.useContext(RbActionsContext);

  const [open, setOpen] = React.useState(false);

  const buttonRef = React.useRef();

  const buttonContextValue = React.useMemo(() => {
    return {
      compacted,
      handleCloseMore: () => {},
      insideMore,
      color,
    };
  }, [insideMore, compacted, color]);

  const contextValue = React.useMemo(() => {
    return {
      compacted: false,
      insideMore: true,
      color,
    };
  }, [color]);

  const handleItemClick = (item) => {
    if (onSelect) {
      onSelect(item);
    }
    setOpen(false);
    if (buttonRef.current) {
      buttonRef.current.focus();
    }
  };

  const handleClick = (event) => {
    setOpen(true);
    if (onClick) {
      onClick(event);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <RbActionsContext.Provider value={buttonContextValue}>
        <RbButtonAction
          {...other}
          aria-expanded={open}
          ref={ref || buttonRef}
          onClick={handleClick}
        />
      </RbActionsContext.Provider>
      <RbPopper
        autoFocus={false}
        open={open}
        anchorEl={buttonRef.current}
        placement={insideMore ? "right-start" : "bottom-end"}
        onClose={handleClose}
      >
        <RbActionsContext.Provider value={contextValue}>
          <Paper>
            <div className={classes.menu} role="menu">
              {items.map((item) => (
                <button
                  key={item.label}
                  className={clsx({
                    [classes.menuItem]: true,
                    [classes.menuItem_selected]:
                      selectedItem?.label === item?.label,
                  })}
                  onClick={() => handleItemClick(item)}
                  role="menuitemradio"
                  aria-checked={selectedItem?.label === item?.label}
                  type="button"
                  autoFocus={selectedItem?.label === item?.label}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </Paper>
        </RbActionsContext.Provider>
      </RbPopper>
    </>
  );
});

RbMenuAction.propTypes = {
  /** Array of items with label and value. */
  items: PropTypes.array,
  /** Callback fired when some item is selected. */
  onSelect: PropTypes.func,
  /** Callback fired when the main button is clicked. */
  onClick: PropTypes.func,
  /** The item selected. */
  selectedItem: PropTypes.object,
};

RbMenuAction.defaultProps = {
  items: [],
  onSelect: undefined,
  onClick: undefined,
  selectedItem: undefined,
};

RbMenuAction.displayName = "RbMenuAction";

export default React.memo(RbMenuAction);
